import React from "react";
import { TableRow, TableCell, Checkbox, Typography, useTheme, alpha } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Worker } from "../../models/worker";
import WorkerNameCell from "./worker-row/WorkerNameCell";
import WorkerStatusCell from "./worker-row/WorkerStatusCell";
import WorkerAvailabilityCell from "./worker-row/WorkerAvailabilityCell";
import WorkerNationalityCell from "./worker-row/WorkerNationalityCell";
import WorkerLanguageCell from "./worker-row/WorkerLanguageCell";
import WorkerProfessionCell from "./worker-row/WorkerProfessionCell";
import WorkerActionsCell from "./worker-row/WorkerActionsCell";

interface WorkerRowProps {
    worker: Worker;
    bulkSelectMode: boolean;
    isSelected: boolean;
    onSelectRow: () => void;
}

/**
 * One row of the worker table. A thin orchestrator: the row shell (selection +
 * click-through to the detail page) plus one focused cell component per column.
 * Column order must match `headCells` in WorkersTableContent.
 */
const WorkerTableRow: React.FC<WorkerRowProps> = ({ worker, bulkSelectMode, isSelected, onSelectRow }) => {
    const theme = useTheme();
    const navigate = useNavigate();

    return (
        <TableRow
            hover
            selected={isSelected}
            sx={{
                '&.Mui-selected': { backgroundColor: alpha(theme.palette.primary.main, 0.08) },
                cursor: 'pointer',
            }}
            onClick={bulkSelectMode ? onSelectRow : () => navigate(`/workers/${worker.workerId}`)}
        >
            {bulkSelectMode && (
                <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                    <Checkbox checked={isSelected} onChange={onSelectRow} color="primary" />
                </TableCell>
            )}

            <WorkerNameCell worker={worker} />
            <WorkerStatusCell worker={worker} />
            <WorkerAvailabilityCell worker={worker} />
            <WorkerNationalityCell worker={worker} />
            <WorkerLanguageCell worker={worker} />
            <WorkerProfessionCell worker={worker} />

            {/* Recruiter */}
            <TableCell>
                {worker.recruiter ? (
                    <Typography variant="body2">{worker.recruiter.username}</Typography>
                ) : (
                    <Typography variant="body2" color="text.secondary">-</Typography>
                )}
            </TableCell>

            {/* Account Manager */}
            <TableCell>
                {worker.accountManager ? (
                    <Typography variant="body2">{worker.accountManager.username}</Typography>
                ) : (
                    <Typography variant="body2" color="text.secondary">-</Typography>
                )}
            </TableCell>

            <WorkerActionsCell worker={worker} />
        </TableRow>
    );
};

export default WorkerTableRow;
